import { useQueryClient } from '@tanstack/react-query';
import type { ColDef, RowClickedEvent, ValueFormatterParams } from 'ag-grid-community';
import type { AxiosError } from 'axios';
import { DateTime } from 'luxon';
import {
  type FunctionComponent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Badge, Button, Col, Container, Overlay, Popover, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import CheckIcon from '../../../assets/check-checkbox.svg';
import DropDownIcon from '../../../assets/drop-down-icon.svg';
import DropDownOpenIcon from '../../../assets/drop-down-open.svg';
import UnCheckIcon from '../../../assets/uncheck-checkbox.svg';
import { config } from '../../../modules/config';
import type { SuccessEnvelop } from '../../../modules/context/guestAuthContext';
import { UserPropertiesContext } from '../../../modules/context/userPropertiesContext';
import { ReservationState } from '../../../modules/enums/types';
import {
  type ErrorEnvelop,
  useAuth,
  useLoading,
  useManualQueryRequest,
  useNotifications,
} from '../../../modules/hooks';
import type {
  MewsOrderItem,
  Reservations,
  ReservationsAPIResponse,
} from '../../../modules/interfaces';
import { endpoints, routes } from '../../../modules/mappers/urls';

import { numberSort, roomSort } from '../../../modules/utils/tableSorts';
import { getApiErrorMessage } from '../../../modules/utils/transform';
import { ReservationGroupModal } from '../../Modals/ReservationGroupModal';
import { SynchronizeModal } from '../../Modals/SynchronizeModal/SynchronizeModal';
import { BadgeCellRenderer, DataTable } from '../DataTable';

interface ReservationTableProps {
  fromGrouping?: boolean;
}

type RefetchParams = {
  status: string;
  Cursor?: string;
};

export const ReservationsTable: FunctionComponent<ReservationTableProps> = ({
  fromGrouping = false,
}: ReservationTableProps) => {
  const navigate = useNavigate();
  const { setSimpleToasts } = useNotifications()!;
  const [searchString] = useState<string>('');
  const [tableData, setTableData] = useState<Reservations[]>([]);
  const [cursor, setCursor] = useState<string>('');
  const { setLoading } = useLoading()!;
  const { credentialsInfo } = useAuth()!;
  const statusArray = [
    ReservationState.PROCESSED,
    ReservationState.STARTED,
    ReservationState.CONFIRMED,
  ];
  const { selectedProperty } = useContext(UserPropertiesContext)!;
  const queryClient = useQueryClient();
  const [showReservationGroupModal, setShowReservationGroupModal] = useState(false);
  const [showSynchronizeModal, setShowSynchronizeModal] = useState(false);
  const [statusShow, setStatusShow] = useState(false);
  const [refreshHandlerFlag, setRefreshHandlerFlag] = useState(false);
  const [desynchronizedReservations, setDesynchronizedReservations] = useState<
    Reservations[]
  >([]);
  const statusTarget = useRef(null);
  const [statusOptions, setStatusOptions] = useState<string[]>([]);

  const [{ data, loading, error }, refetch] =
    useManualQueryRequest<ReservationsAPIResponse>(
      endpoints.MEWS_RESERVATIONS,

      {
        authToken: credentialsInfo?.token,
        params: {
          status: statusOptions.length === 0 ? '' : statusOptions.join(','),
        },
        propertyId: selectedProperty?.property.uuid,
      },
      { manual: true },
    );

  const refreshHandler = async () => {
    const params: RefetchParams = {
      status: statusOptions.length === 0 ? '' : statusOptions.join(','),
    };

    if (cursor) {
      params.Cursor = cursor;
    }

    setRefreshHandlerFlag(true);

    refetch(params);
  };

  const countDesynchronizedReservations = (reservations: Reservations[]) => {
    const missingInternalReservationIds = reservations.filter(
      (reservation) =>
        (!reservation.InternalReservation ||
          !reservation.InternalReservation.conversationCreated) &&
        (reservation.State === ReservationState.CONFIRMED ||
          reservation.State === ReservationState.STARTED),
    );

    return missingInternalReservationIds;
  };

  const onApplyStatus = () => {
    setTableData([]);
    setStatusShow(false);
    refetch({
      status: statusOptions.length === 0 ? '' : statusOptions.join(','),
    });
  };

  const onStatusItemClick = (statusItem: string, isSelected: boolean) => {
    if (isSelected) {
      const newList = statusOptions.filter((item) => item !== statusItem);
      setStatusOptions(newList);
    } else {
      const newList = [...statusOptions];
      newList.push(statusItem);
      setStatusOptions(newList);
    }
  };

  const renderStatusItem = (statusItem: string) => {
    const isSelected = statusOptions.includes(statusItem);
    return (
      <div
        className="drop-down-status-item"
        style={{ backgroundColor: isSelected ? '#F6F6F6' : '#FFFFFF' }}
        onClick={() => onStatusItemClick(statusItem, isSelected)}
      >
        <img
          className="checkbox-icon"
          src={isSelected ? CheckIcon : UnCheckIcon}
          alt="check-icon"
        />
        <div>{statusItem}</div>
      </div>
    );
  };

  const onCancleStatus = () => {
    setStatusOptions([]);
    setStatusShow(false);
  };

  useEffect(() => {
    const cachedData = queryClient.getQueryData<SuccessEnvelop<ReservationsAPIResponse>>([
      'v2/reservations',
      'get',
    ]);

    if (cachedData) {
      const { result } = cachedData;
      setTableData((prevData: Reservations[]) => prevData.concat(result.Reservations));

      setCursor(result.Cursor);
    }

    if (selectedProperty) {
      refetch();
    }
  }, [selectedProperty]);

  useEffect(() => {
    const isLoading = loading;
    setLoading(isLoading);
  }, [loading, setLoading]);

  useEffect(() => {
    if (error) {
      setTableData([]);
      const message = getApiErrorMessage(error as AxiosError<ErrorEnvelop>);
      setSimpleToasts({ type: 'danger', message, show: true });
      return;
    }

    if (data && data.result.Reservations.length > 0) {
      const { result } = data;

      if (refreshHandlerFlag) {
        setTableData((prevData: Reservations[]) => prevData.concat(result.Reservations));
        setRefreshHandlerFlag(false);
      } else {
        setTableData(() => result.Reservations);
      }

      setCursor(result.Cursor);

      const desynchronizedIds = countDesynchronizedReservations(result.Reservations);
      setDesynchronizedReservations(desynchronizedIds);
    }
  }, [data, error, setSimpleToasts]);

  const columns = useMemo(() => {
    const baseColumns: ColDef<Reservations>[] = [
      {
        valueGetter: (params) => {
          const { data: reservation } = params;
          const resource = reservation?.Resource;
          const name = resource
            ? `${resource.Name} -  ${resource.ResourceName}`
            : 'Not assigned';
          return name;
        },
        headerName: 'ROOM',
        sortable: true,
        comparator: (valueA, valueB) => roomSort(valueA, valueB),
        flex: 1,
      },
    ];

    const conditionalColumns: ColDef<Reservations>[] = fromGrouping
      ? [
          {
            field: 'GroupName',
            headerName: 'GROUP NAME',
            sortable: true,
            flex: 1,
          },
          {
            field: 'Rooms',
            headerName: 'ROOMS',
            sortable: true,
            flex: 1,
          },
          {
            field: 'PrimaryGuest',
            headerName: 'PRIMARY GUEST',
            sortable: true,
            flex: 1,
          },
        ]
      : [
          {
            valueGetter: (params) => {
              const { data: reservation } = params;
              const customer = reservation?.Customer;
              const name = customer ? `${customer.FirstName} ${customer.LastName}` : '-';
              return name;
            },
            headerName: 'GUEST NAME',
            sortable: true,
            flex: 1,
          },
        ];

    const otherColumns: ColDef<Reservations>[] = [
      {
        field: 'ScheduledStartUtc',
        headerName: 'ARRIVAL DATE',
        sortable: true,
        valueFormatter: (params: ValueFormatterParams<Reservations, string>) => {
          const { value } = params;
          return DateTime.fromISO(value || '').toFormat('dd LLL yyyy');
        },
        cellStyle: { color: '#757474' },
        flex: 1,
      },
      {
        field: 'EndUtc',
        headerName: 'DEPARTURE DATE',
        sortable: true,
        valueFormatter: (params: ValueFormatterParams<Reservations, string>) => {
          const { value } = params;
          return DateTime.fromISO(value || '').toFormat('dd LLL yyyy');
        },
        cellStyle: { color: '#757474' },
        flex: 1,
      },
      {
        field: 'OrderItem',
        headerName: 'AMOUNT',
        sortable: true,
        comparator: (valueA, valueB) =>
          numberSort(valueA.Amount.NetValue, valueB.Amount.NetValue),
        valueFormatter: (params: ValueFormatterParams<Reservations, MewsOrderItem>) => {
          const { value } = params;
          return (
            value?.Amount?.NetValue?.toLocaleString('en-US', {
              style: 'currency',
              currency: 'USD',
            }) || `$0.00`
          );
        },
        flex: 1,
      },
      {
        field: 'State',
        headerName: 'STATUS',
        sortable: true,
        cellRenderer: BadgeCellRenderer,
        flex: 1,
      },
    ];

    return [...baseColumns, ...conditionalColumns, ...otherColumns];
  }, [fromGrouping]);

  return (
    <>
      <DataTable
        refreshHandler={refreshHandler}
        addButtonFunction={
          fromGrouping ? () => setShowReservationGroupModal(true) : undefined
        }
        buttonFunction={!fromGrouping ? () => setShowSynchronizeModal(true) : undefined}
        component="reservations"
        addButtonText="New reservation"
        buttonContent={
          <div>
            {'Synchronize '}
            <Badge
              text="dark"
              style={{
                fontSize: '10px',
                marginLeft: '2px',
                marginRight: '2px',
                padding: '3px',
              }}
              bg="danger"
            >
              {desynchronizedReservations.length}
            </Badge>
          </div>
        }
        searchString={searchString}
        changeState={0}
        rowData={tableData}
        columnDefs={columns}
        pagination
        paginationPageSize={config.tables.reservations.paginationSize}
        leftSideContent={
          <>
            <div className="artical-dropdown-container ">
              <div
                ref={statusTarget}
                onClick={() => setStatusShow(!statusShow)}
                className="category-text-container"
              >
                <div className="category-text-style">
                  {statusOptions.length === 0
                    ? 'Confirmed and Started'
                    : statusOptions[0]}
                  {statusOptions.length > 1 && ' and '}
                  {statusOptions.length > 1 && (
                    <div className="more-text-in-dropdown">
                      +{statusOptions.length - 1} More
                    </div>
                  )}
                </div>
                <img src={statusShow ? DropDownOpenIcon : DropDownIcon} alt="drop-down" />
              </div>
            </div>
            <Overlay
              onHide={() => setStatusShow(false)}
              target={statusTarget.current}
              rootClose
              show={statusShow}
              placement="right"
            >
              <Popover id="popover-positioned-bottom">
                <Popover.Body className="property_overlay">
                  <div className="drop-down-container-status">
                    {statusArray.map(renderStatusItem)}
                    <div className="apply-and-cancle-container">
                      <Button onClick={onCancleStatus} className="cancle-text-container">
                        Cancel
                      </Button>
                      <Button onClick={onApplyStatus} className="apply-text-container">
                        Apply
                      </Button>
                    </div>
                  </div>
                </Popover.Body>
              </Popover>
            </Overlay>
          </>
        }
        onRowClicked={(e: RowClickedEvent<Reservations>) => {
          if (e.api.getFocusedCell()?.column.getColId() === 'action') {
            return;
          }

          const { data: reservation } = e;

          if (reservation) {
            const { Id: reservationId } = reservation;

            navigate(
              routes.RESERVATION_DETAILS.replace(':reservationId', reservationId),
              {
                state: {
                  reservationId,
                },
              },
            );
          }
        }}
      />
      {showReservationGroupModal && (
        <ReservationGroupModal
          show={showReservationGroupModal}
          handleClose={() => setShowReservationGroupModal(false)}
        />
      )}
      {showSynchronizeModal && (
        <SynchronizeModal
          selectedReservations={desynchronizedReservations}
          show={showSynchronizeModal}
          handleClose={() => setShowSynchronizeModal(false)}
          refreshHandler={refreshHandler}
        />
      )}
      <Container className="table-header" fluid style={{ padding: '16px' }}>
        <Row className="m-0 justify-content-end w-100 align-items-center">
          <Col className="p-0">
            <Button className="float-right" onClick={refreshHandler}>
              Load More
            </Button>
          </Col>
        </Row>
      </Container>
    </>
  );
};
